import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { UtilService } from '@app/core/services/utils/util.service';
import { ApiService } from '../../../core/services/api/api.service';
import { Filter, FilterTypes, QueryParameters, SortField } from '@app/core/entities/query-parameters.class';
import {
  deletefilter,
  formatShippingAddressString,
  formattedDate,
  TABLES,
  formatShippingNameString,
  modifyDateRangeValuesByColumn,
} from 'src/utils';
import { FreeSearchParameters } from '@app/core/entities/free-search-parameters.class';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { OrderService } from '@app/services/order/order.service';
import { Order } from '@app/services/order/order.model';
import {
  ColumnSettings,
  GroupAction,
  RowAction,
  tableMessages,
  TableFilterType,
} from '@app/sharedModule/models/table-settings.model';
import { deleteTableConfig } from '../configs/delete.table.config';
import { Store } from '@ngrx/store';
import { DeletedOrdersSelectors } from '../store/selectors';
import { DeletedOrdersActions } from '../store/actions';
import { MatDialog } from '@angular/material/dialog';
import { RestoreOrderDialog } from '@app/pages/deleted-orders/dialogs/restore-order.dialog/restore-order.dialog';
import { Actions, ofType } from '@ngrx/effects';
import { EventService } from '@app/core/services/event/event.service';
import { ActivatedRoute } from '@angular/router';
import { WSEventData } from '@core/services/web-socket/web-socket.model';
import { WebsocketSubscriberService } from '@core/services/websocket-subscriber/websocket.subscriber.service';
import { SidePanelService } from '@app/features/side-panel/side-panel.service';
import { TableService } from '@app/services/table/table.service';
import { getDefaultSortField } from '@utils/shop-utils';
import { ShopService } from '@services/shops/shop.service';
import { ColorCoding } from '@pdqdev/pdq-shared/dist/entities';
import { assignColorCodingToRows } from '@utils/color-coding.utils';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { DeliverCursorPaginationService } from '@app/services/deliver-cursor-pagination/deliver-cursor-pagination.service';
import { DeletedOrdersCursorPaginationService } from '../services/deleted-orders-cursor-pagination.service';
import { PaginationCursorAction } from '@app/storybook/layouts/components/footer/pagination/pagination.component';
@Component({
  selector: 'app-delete-deliveries',
  templateUrl: './deleted-orders.component.html',
  styleUrls: ['./deleted-orders.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [{ provide: DeliverCursorPaginationService, useClass: DeletedOrdersCursorPaginationService }],
})
export class DeletedOrdersComponent implements OnInit, OnDestroy {
  readonly TABLES = TABLES;

  private orders$ = this.store.select(DeletedOrdersSelectors.selectOrders);
  private totalCountOrders$ = this.store.select(DeletedOrdersSelectors.selectTotalCountOrders);
  private ordersLoading$ = this.store.select(DeletedOrdersSelectors.selectOrdersLoading);
  private orderIdSearchParams: string = null;
  private ordersSubscription$: Subscription;
  private totalCountOrdersSubscription$: Subscription;
  private ordersLoadingSubscription$: Subscription;
  private sortFields: SortField[] | 'NONE';
  private tableFilters: Filter[];
  private destroyed$ = new Subject<any>();
  private queryParamsSubscription$: Subscription;

  columns: ColumnSettings[] = [...deleteTableConfig];
  rowActions = [RowAction.RESTORE_ORDER];
  groupActions = [GroupAction.RESTORE_ORDERS];

  temp = [];
  rows = [];
  totalCountOrders: number;
  totalPages: number = 1;
  activePage: number = 1;
  ordersPerPage = 50;
  allPages = {};
  yellowPB = false;
  loading: boolean;
  searchToggleButtons = false;
  rowSelectOptions = [];
  showFreeSearchSuggestions = false;
  showFreeSearchClearButton = false;
  freeSearchValue: any = '';
  freeSearchSelectedField = 'orderNumber';
  filter = 'deleted';
  filterActive = false;
  selectedOrders = [];
  updateOrderSubscription$: Subscription;
  deliverCursorPagination = inject(DeliverCursorPaginationService) as DeliverCursorPaginationService<
    Record<string, never>
  >;
  useCursorPagination = this.deliverCursorPagination.useCursorPagination;
  focusedOrderId: string | null = null;
  colorCodingData: ColorCoding[] = [];

  constructor(
    private cookieService: CookieService,
    public utilService: UtilService,
    public apiService: ApiService,
    private orderService: OrderService,
    private store: Store,
    private dialog: MatDialog,
    private actions$: Actions,
    private eventService: EventService,
    private route: ActivatedRoute,
    private wsSubscriber: WebsocketSubscriberService,
    private spService: SidePanelService,
    private tableService: TableService,
    private shopService: ShopService,
    private toastService: ToastService,
  ) {
    this.ordersPerPage = this.orderService.getOrderPerPage();
    this.detectRestoreOrderSuccess();
    this.queryParamsOrderJump();
  }

  ngOnInit(): void {
    this.loadDeletedOrders();
    this.updateOrdersListener();
    this.initShopService();
    this.initSidePanelService();
  }

  initShopService() {
    this.shopService.shop$.pipe(takeUntil(this.destroyed$)).subscribe((shop) => {
      this.getPagedOrders(0, this.ordersPerPage);
      this.colorCodingData = shop.colorCoding;
    });
  }

  initSidePanelService(): void {
    this.spService.focusedOrderId$.pipe(takeUntil(this.destroyed$)).subscribe(({ focusedId }) => {
      this.focusedOrderId = focusedId ?? null;
    });
  }

  initTableService(): void {
    this.tableService.popoverPressedRowId$.pipe(takeUntil(this.destroyed$)).subscribe((rowId) => {
      this.focusedOrderId = rowId;
    });
  }

  updateOrdersListener(): void {
    this.updateOrderSubscription$ = this.wsSubscriber
      .fireVerifyOrdersCounterChanged()
      .pipe()
      .subscribe((data: WSEventData) => {
        if (data.orders) {
          this.updateOrders(data.orders);
        }
      });
  }

  updateOrders(orders: Order[]): void {
    let currentOrders: Order[];

    if (orders?.length < 1) {
      return;
    }

    this.orders$.subscribe((observableOrders) => {
      currentOrders = JSON.parse(JSON.stringify(observableOrders));

      orders.forEach((order) => {
        this.tableService.deleteOrderCachedMap(order.id);
        for (let i = 0; i < currentOrders.length; i++) {
          if (currentOrders[i].id == order.id) {
            currentOrders[i] = { ...order };
          }
        }
      });
    });

    this.store.dispatch(
      DeletedOrdersActions.loadDeletedOrdersSuccess({ orders: currentOrders, count: currentOrders.length }),
    );
  }

  queryParamsOrderJump(): void {
    this.queryParamsSubscription$ = this.route.queryParams.subscribe((queryParam: any) => {
      this.orderIdSearchParams = queryParam.search;
      if (this.orderIdSearchParams) {
        this.filter = FilterTypes.QUERY_PARAMS;
        this.getPagedOrders(0, this.ordersPerPage);
      }
    });
  }

  detectRestoreOrderSuccess(): void {
    this.actions$
      .pipe(
        takeUntil(this.destroyed$),
        ofType(DeletedOrdersActions.restoreOrdersSuccess, DeletedOrdersActions.restoreOrdersFailure),
      )
      .subscribe(() => {
        this.getPagedOrders(0, this.ordersPerPage);
      });
  }

  loadDeletedOrders() {
    this.ordersSubscription$ = this.orders$.subscribe((orders: Order[]) => {
      if (!orders || !orders.length) {
        orders = [];
      }
      this.temp = this.processOrders(orders);
    });
    this.totalCountOrdersSubscription$ = this.totalCountOrders$.subscribe((totalCountOrders: number) => {
      this.totalCountOrders = totalCountOrders;
      this.totalPages = Math.ceil(totalCountOrders / this.ordersPerPage);
      this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    });
    this.ordersLoadingSubscription$ = this.ordersLoading$.subscribe((ordersLoading: boolean) => {
      this.loading = ordersLoading;
    });
  }

  clearFreeSearchFilter(): void {
    this.showFreeSearchSuggestions = false;
    this.searchToggleButtons = false;
    this.freeSearchValue = '';
    this.filter = 'deleted';
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  showFreeSearchResults(value: string): void {
    this.showFreeSearchSuggestions = false;
    this.filter = 'freesearch';
    this.freeSearchValue = value;
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  getPagedOrders(page, maxsize, sorting: SortField[] | 'NONE' = null): void {
    if (this.useCursorPagination()) {
      this.pullOrdersByCursor(page === 0 ? 'first' : 'current', sorting);
      return;
    }

    const queryParameters = this.buildQueryParameters(page, sorting);
    this.store.dispatch(
      DeletedOrdersActions.loadDeletedOrders({
        queryParameters,
      }),
    );
  }

  private buildQueryParameters(page: number, sorting: SortField[] | 'NONE' = null): QueryParameters {
    const queryParameters = sorting ? this.createQueryParameters(page, sorting) : this.createQueryParameters(page);
    if (this.filter === 'freesearch') {
      const filter = this.getFreeSearchFilter();
      queryParameters.filters = [...queryParameters.filters, filter];
    } else if (this.filter === 'freetext') {
      queryParameters.search = this.freeSearchValue;
    } else if (this.filter === 'multiple') {
      const filter = this.getMultipleFilter();
      queryParameters.filters = [filter];
    }

    if (this.tableFilters && this.tableFilters.length) {
      queryParameters.filters = [...queryParameters.filters, ...this.tableFilters];
    }

    switch (this.filter) {
      case FilterTypes.QUERY_PARAMS: {
        queryParameters.filters.push(new Filter('order.id', 'EQUAL', [this.orderIdSearchParams]));
        break;
      }
    }

    return queryParameters;
  }

  private pullOrdersByCursor(action: PaginationCursorAction = 'first', sorting: SortField[] | 'NONE' = null): void {
    this.loading = true;
    this.store.dispatch(DeletedOrdersActions.setDeletedOrdersLoading({ loading: true }));
    const query = this.buildQueryParameters(0, sorting);

    this.deliverCursorPagination
      .fetchOrders(action, query, this.ordersPerPage)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();

    if (action === 'first') {
      this.deliverCursorPagination.fetchTotalCount(query);
    }
  }

  onCursorPageChange(action: PaginationCursorAction): void {
    this.pullOrdersByCursor(action, this.sortFields);
  }

  getMultipleFilter(): Filter {
    return new Filter(this.freeSearchSelectedField, 'IN', this.freeSearchValue);
  }

  showFreeTextSearchResults(): void {
    this.showFreeSearchSuggestions = false;
    this.filter = 'freetext';
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  getFreeSearchSuggestions(event): Observable<any[]> {
    if (event.length == 0) {
      this.showFreeSearchClearButton = false;
      this.searchToggleButtons = false;
      this.showFreeSearchSuggestions = false;
      return EMPTY;
    }
    this.showFreeSearchClearButton = true;
    this.showFreeSearchSuggestions = true;
    this.searchToggleButtons = true;
    return this.orderService.getSearchOrders(
      new FreeSearchParameters(this.freeSearchValue, [this.freeSearchSelectedField], deletefilter(1)),
    ) as Observable<any[]>;
  }

  createQueryParameters(
    page: number,
    sortFields: SortField[] | 'NONE' = [new SortField(getDefaultSortField(this.shopService.shopPlatform), 'ASC')],
  ): QueryParameters {
    const queryParameters = new QueryParameters();
    queryParameters.filters = [new Filter('deleted', 'IN', ['1'])];
    queryParameters.page = page;
    queryParameters.maxResults = this.ordersPerPage;
    queryParameters.anyAll = 'all';
    queryParameters.sortFields = sortFields == 'NONE' ? undefined : sortFields;
    return queryParameters;
  }

  getFreeSearchFilter(): Filter {
    return new Filter(this.freeSearchSelectedField, 'IN', [this.freeSearchValue]);
  }

  processOrders(rows: any): Order[] {
    return rows.map((row) => {
      row = { ...row };

      if (row.createdDate) {
        row.createdDateFormatted = formattedDate(row.createdDate);
      }

      if (row.deletedDate) {
        row.deletedDate = formattedDate(row.deletedDate);
      }

      // backwards compatibility who delete when funnel stage named Preview
      if (row.comment?.includes('Preview')) {
        row.comment = row.comment.replace('Preview', 'Verify');
      }

      if (row?.comment) {
        const currentComment = row.comment?.split('|');
        // the comment format for now is
        // old text... |printCounter|lastTimePrinted|batchName
        // because we need to converted last time printed to current browser time
        if (currentComment?.length > 1) {
          const printCounter = currentComment[1];
          const lastPrintTime = currentComment[2];
          const batchName = currentComment[3];
          const browserTime = new Date(lastPrintTime).toLocaleString();

          row.comment = `Printed, ${printCounter} times at: ${formattedDate(browserTime)} Batch Name: ${batchName}`;
        }
      }

      row.formattedShippingCityAndProvince = formatShippingAddressString(row);
      row.foramttedShippingName = formatShippingNameString(row);

      row.deletedBy = row?.deletedBy ?? 'View in side panel';
      row.currentColorTooltipString =
        !row.colorCoding || row.colorCoding.includes('transparent')
          ? ''
          : row.colorCoding
              .map((colorValue) => {
                const current = this.colorCodingData?.find((colorData) => colorData.value === colorValue);
                return current?.label || current?.defaultName || '';
              })
              .join(', ');

      return row;
    });
  }

  async onTableActivate({ row }) {
    if (!row.id) {
      return;
    }

    this.spService.setFocusedOrderId(row.id);
  }

  onChangeTableFilter(columnIndex: number): void {
    this.tableFilters = [];
    this.sortFields = undefined;
    let fieldName;

    this.columns.forEach((column: ColumnSettings) => {
      if (!column.filterType || !column.filterOption || (!column.filterValue && !column.filterValueArray?.length)) {
        return;
      }

      fieldName = column.filterFields ? column.filterFields : column.prop;
      let filterValue = [column.filterValue];

      if (column.filterType === TableFilterType.COLOR) {
        filterValue = column.filterValueArray;
      } else if (column.filterOption === 'DATE RANGE' || column.filterOption === 'DATE RANGE STRICT') {
        filterValue = [];
        if (column.filterValueStart) {
          filterValue.push(column.filterValueStart);
        }
        if (column.filterValueEnd) {
          const endDate = new Date(Date.parse(column.filterValueEnd));
          endDate.setDate(endDate.getDate() + 1);
          modifyDateRangeValuesByColumn(column.prop, { endDate: endDate });
          filterValue.push(endDate.toISOString());
        }
      } else if (column.filterOption === 'LIKE') {
        // if we have to add a sort field in the future, consider use id instead of createdDate for better performance
        this.sortFields = 'NONE';
      }

      if (column.filterType !== TableFilterType.TAGS) {
        this.tableFilters.push(
          new Filter(
            fieldName,
            column.filterOption,
            filterValue.map((x) => (x?.trim ? x.trim() : x)),
          ),
        );
      }

      if (column.filterType === TableFilterType.TAGS && column.filterValueArray?.length > 0) {
        column.filterValueArray.forEach((filterValue) => {
          this.tableFilters.push(new Filter(filterValue.type, column.filterOption, [filterValue.name]));
        });
      }
    });

    this.filterActive = fieldName ? true : false;
    this.activePage = 1;
    this.getPagedOrders(this.activePage - 1, this.ordersPerPage);
  }

  onChangeOrdersPerPage(ordersPerPage: number): void {
    this.ordersPerPage = ordersPerPage;
    this.cookieService.set('orderperpage_cookie', this.ordersPerPage.toString());
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  onSearchToTable(event: { filter: string; searchTerm: string | number; searchField: string }): void {
    this.freeSearchValue = event.searchTerm;
    this.filter = event.filter;
    this.freeSearchSelectedField = event.searchField;
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  jumpToPage(page: number): void {
    if (!page) {
      alert('Please enter a valid number');
      return;
    }

    if (page > this.totalPages || page < 1) {
      alert(`Please enter a Page no. less than ${this.totalPages}`);
      return;
    }

    this.activePage = page;
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.getPagedOrders(this.activePage - 1, this.ordersPerPage);
  }

  sortColumns(sortOptions): void {
    this.columns = this.columns.map((column: ColumnSettings, i: number) => {
      if (i !== sortOptions.index) {
        return { ...column, sortDirection: null };
      }

      const sortDirection = sortOptions.sortDirection;
      const sortFieldNames: string[] = column.sortFieldName ? column.sortFieldName : [column.prop];

      this.sortFields = sortFieldNames.map((fieldName: string) => new SortField(fieldName, sortDirection));

      return {
        ...column,
        sortDirection,
      };
    });

    this.getPagedOrders(this.activePage - 1, this.ordersPerPage, this.sortFields);
  }

  paginate(page: number): void {
    this.activePage = page + 1;
    this.getPagedOrders(page, this.ordersPerPage);
  }

  chooseEmptyMessage() {
    return this.filterActive || this.filter == 'multiple'
      ? tableMessages.NO_RESULT_MESSAGE
      : tableMessages.EMPTY_MESSAGE;
  }

  onSelect(selectedOrders: Order[]): void {
    this.selectedOrders = selectedOrders;
  }

  onRestoreOrder(orders: Order[]): void {
    orders = orders ?? this.selectedOrders;
    const ordersId: string[] = orders.map((res) => res.id);
    const dialogRef = this.dialog.open(RestoreOrderDialog, {
      panelClass: 'restore-orders-dialog',
      backdropClass: 'global_modal_overlay',
      data: {
        count: orders.length,
      },
    });
    dialogRef.componentInstance.onRestore?.subscribe(() => {
      this.store.dispatch(
        DeletedOrdersActions.restoreOrders({
          ordersId: ordersId,
        }),
      );
    });
    dialogRef.afterClosed().subscribe(() => {
      this.selectedOrders = [];
    });
  }

  changeColorCoding({ order, colors, selectedOrders }) {
    const orderIds: string[] = selectedOrders ? this.selectedOrders.map((order) => order.id) : [order.id];
    const previousRows = this.temp;
    this.assignColorsCodingUpdate(orderIds.map((id) => ({ id, colorCoding: colors })));
    this.orderService.updateOrdersColorCoding(orderIds, colors).subscribe({
      next: (succededOrders) => {
        if (succededOrders && succededOrders?.length < orderIds.length) {
          const rowsToRevert = previousRows.filter(
            (row) => !succededOrders.some((succededOrder) => succededOrder.id === row.id),
          );
          this.assignColorsCodingUpdate(rowsToRevert);
        }
      },
      error: (error) => {
        this.assignColorsCodingUpdate(previousRows);
        this.toastService.showToast({
          message: `Failed to update color coding`,
          mode: 'danger',
        });
      },
    });
  }

  private assignColorsCodingUpdate(updatedOrders: Pick<Order, 'id' | 'colorCoding'>[]) {
    const newRows = assignColorCodingToRows(this.temp, updatedOrders, this.colorCodingData);

    this.selectedOrders = [...newRows.filter((order) => this.selectedOrders.some((o) => o.id === order.id))];

    const spOrder = this.spService.getActiveOrder();
    if (spOrder?.id) {
      const spColorCoding = updatedOrders.find((order) => order.id === spOrder?.id)?.colorCoding;
      this.spService.setActiveOrder({ ...spOrder, colorCoding: spColorCoding });
    }
    this.temp = [...newRows];
  }

  ngOnDestroy(): void {
    this.ordersSubscription$.unsubscribe();
    this.ordersLoadingSubscription$.unsubscribe();
    this.totalCountOrdersSubscription$.unsubscribe();
    this.destroyed$?.next(true);
    this.destroyed$?.complete();
    this.updateOrderSubscription$?.unsubscribe();
  }
}
